// Player interface language (buttons, labels). Content (excursions, steps) is translated
// in the admin and comes from the server in the chosen language (X-Lang header).
import { useEffect, useState } from 'react';
import { TASK_STRINGS } from './i18nTasks';

const LS_KEY = 'vod_lang';
const RTL = ['ar', 'he', 'fa', 'ur'];
export const UI_LANGS = ['en', 'ru', 'ar'];

export function getLang() {
  try {
    const l = localStorage.getItem(LS_KEY);
    if (l) return l;
  } catch (e) {}
  return 'en';
}

export function applyDir(lang) {
  document.documentElement.lang = lang;
  document.documentElement.dir = RTL.includes(lang) ? 'rtl' : 'ltr';
}

export function setLang(lang) {
  try { localStorage.setItem(LS_KEY, lang); } catch (e) {}
  applyDir(lang);
  window.dispatchEvent(new Event('vod-lang'));
}

// Re-render on language change
export function useLang() {
  const [lang, setL] = useState(getLang());
  useEffect(() => {
    const on = () => setL(getLang());
    window.addEventListener('vod-lang', on);
    return () => window.removeEventListener('vod-lang', on);
  }, []);
  return lang;
}

const S = {
  en: {
    tagline: 'Explore\nDubai\ndifferently',
    create_account: 'Create your account',
    create_sub: 'Discover immersive audio walks\nand interactive city adventures',
    welcome_back: 'Welcome back',
    welcome_sub: 'Sign in to continue your\nDubai audio journey',
    your_name: 'Your name',
    email: 'Email',
    password: 'Password',
    agree_1: 'I agree to the ',
    terms: 'Terms of Service',
    agree_and: ' and ',
    privacy: 'Privacy Policy',
    get_started: 'Get started',
    sign_in: 'Sign in',
    or: 'or',
    have_account: 'Already have an account?',
    new_here: 'New to Voice of Dubai?',
    create_account_link: 'Create account',
    forgot: 'Forgot password?',
    err_fill: 'Please enter {fields}.',
    f_name: 'your name',
    f_email: 'a valid email',
    f_password: 'a password (6+ characters)',
    accept_terms: 'Please accept the Terms to continue',
    loading: 'Loading…',
    choose_excursion: 'Choose your excursion',
    explore_listen: 'Explore · Listen · Discover',
    audio_walk: 'Audio walk',
    quest: 'Quest',
    min: '{n} min',
    no_excursions: 'Excursions are coming soon',
    start: 'Start',
    continue_tour: 'Continue',
    view_on_map: 'View on map',
    open_map: 'Open map',
    route: 'Route',
    stops: '{n} stops',
    walk_km: '≈ {km} km walk',
    walk_time: '≈ {min} min on foot',
    open_in_google: 'Open route in Google Maps',
    step_of: 'Step {n} of {total}',
    continue: 'Continue',
    finish: 'Finish',
    check_answer: 'Check answer',
    next: 'Next',
    your_answer: 'Your answer…',
    select_one: 'Select one answer',
    correct: 'Correct!',
    wrong: 'Not quite — try again',
    wrong_sub: 'Try again or tap “Next”',
    attempts_left: 'Wrong. Attempts left: {n}',
    attempts_over: 'No attempts left. Tap “Next”.',
    hint: 'Hint',
    show_hint: 'Show hint (−{n} pts)',
    reveal_answer: 'Show the answer',
    correct_answer: 'Correct answer',
    fun_fact: 'Did you know?',
    video: 'Video',
    offline: 'Offline mode',
    narration: 'Narration for this moment',
    step_not_found: 'Step not found',
    steps_not_loaded: 'Could not load the steps',
    congrats: 'Congratulations!',
    finished_sub: 'You have completed the excursion',
    points_of: '{n} of {max}',
    points: 'points',
    rate: 'Rate the excursion',
    to_excursions: 'Back to excursions',
    thanks_partner: 'Thank you for being with us!',
    rate_title: 'Rate the excursion',
    rate_sub: 'Your feedback helps us get better',
    rate_labels: ['', 'Did not like it', 'So-so', 'Okay', 'Liked it', 'Loved it!'],
    your_comment: 'Your comment',
    comment_ph: 'Share your impressions (optional)',
    send_review: 'Send review',
    sending: 'Sending…',
    skip: 'Skip',
    logout: 'Log out',
    language: 'Language',
    menu: 'Menu',
    my_excursions: 'My excursions',
    plan_cta_title: "Build your own walk",
    plan_cta_sub: "Short on time? Get a route made just for you",
    plan_title: "Build your walk",
    plan_sub: "Answer three questions and we will put together a route for you",
    q_time: "How much time do you have?",
    q_interests: "What interests you?",
    q_interests_hint: "Choose any — or skip",
    q_with: "Who are you with?",
    with_alone: "On my own",
    with_couple: "As a couple",
    with_friends: "With friends",
    with_kids: "With children",
    q_kids: "A game quest for kids or a regular tour?",
    kids_quest: "Kids quest",
    regular_tour: "Regular tour",
    build: "Build my walk",
    building: "Building your walk…",
    your_walk: "Your walk",
    walk_summary: "{n} stops · ≈ {min} min",
    walk_incl: "including ≈ {min} min on foot",
    too_short: "We kept the main points only — the walk may take a little longer",
    kids_fallback: "The kids quest is coming soon — here is the regular tour",
    change_answers: "Change answers",
    key_point: "Main point",
    no_points: "Walks will be available soon",
    t_30: "30 min",
    t_45: "45 min",
    t_60: "1 hour",
    t_90: "1.5 hours",
    t_120: "2 hours",
    you_here: 'Route from where you are now',
    loc_asking: 'Finding your location…',
    loc_denied: 'Allow location access to see where you are on the map',
    loc_unavailable: 'Your location is not available right now',
    loc_refresh: 'Update my location',
    expand: 'Open full screen',
    leave_site: 'You are leaving “{name}” and going to the partner’s website.',
    go: 'Go',
    cancel: 'Cancel',
  },
  ru: {
    tagline: 'Откройте\nДубай\nпо-новому',
    create_account: 'Создайте аккаунт',
    create_sub: 'Иммерсивные аудиопрогулки\nи интерактивные приключения',
    welcome_back: 'С возвращением',
    welcome_sub: 'Войдите, чтобы продолжить\nаудиопутешествие по Дубаю',
    your_name: 'Ваше имя',
    email: 'Email',
    password: 'Пароль',
    agree_1: 'Я принимаю ',
    terms: 'Условия использования',
    agree_and: ' и ',
    privacy: 'Политику конфиденциальности',
    get_started: 'Начать',
    sign_in: 'Войти',
    or: 'или',
    have_account: 'Уже есть аккаунт?',
    new_here: 'Впервые здесь?',
    create_account_link: 'Создать аккаунт',
    forgot: 'Забыли пароль?',
    err_fill: 'Укажите {fields}.',
    f_name: 'имя',
    f_email: 'правильный email',
    f_password: 'пароль (от 6 символов)',
    accept_terms: 'Примите условия, чтобы продолжить',
    loading: 'Загрузка…',
    choose_excursion: 'Выберите экскурсию',
    explore_listen: 'Смотрите · Слушайте · Открывайте',
    audio_walk: 'Аудиопрогулка',
    quest: 'Квест',
    min: '{n} мин',
    no_excursions: 'Экскурсии скоро появятся',
    start: 'Начать',
    continue_tour: 'Продолжить',
    view_on_map: 'Показать на карте',
    open_map: 'Открыть карту',
    route: 'Маршрут',
    stops: 'Точек: {n}',
    walk_km: '≈ {km} км пешком',
    walk_time: '≈ {min} мин пешком',
    open_in_google: 'Открыть маршрут в Google Картах',
    step_of: 'Шаг {n} из {total}',
    continue: 'Дальше',
    finish: 'Завершить',
    check_answer: 'Проверить',
    next: 'Дальше',
    your_answer: 'Ваш ответ…',
    select_one: 'Выберите один ответ',
    correct: 'Верно!',
    wrong: 'Неверно — попробуйте ещё раз',
    wrong_sub: 'Попробуйте ещё раз или нажмите «Дальше»',
    attempts_left: 'Неверно. Осталось попыток: {n}',
    attempts_over: 'Попытки закончились. Нажмите «Дальше».',
    hint: 'Подсказка',
    show_hint: 'Показать подсказку (−{n} б.)',
    reveal_answer: 'Узнать ответ',
    correct_answer: 'Правильный ответ',
    fun_fact: 'Интересный факт',
    video: 'Видео',
    offline: 'Офлайн-режим',
    narration: 'Рассказ к этому моменту',
    step_not_found: 'Шаг не найден',
    steps_not_loaded: 'Не удалось загрузить шаги',
    congrats: 'Поздравляем!',
    finished_sub: 'Вы прошли экскурсию',
    points_of: '{n} из {max}',
    points: 'баллов',
    rate: 'Оценить экскурсию',
    to_excursions: 'К экскурсиям',
    thanks_partner: 'Спасибо, что были с нами!',
    rate_title: 'Оцените экскурсию',
    rate_sub: 'Ваше мнение помогает нам становиться лучше',
    rate_labels: ['', 'Совсем не понравилось', 'Так себе', 'Нормально', 'Понравилось', 'Очень понравилось!'],
    your_comment: 'Ваш комментарий',
    comment_ph: 'Поделитесь впечатлениями (необязательно)',
    send_review: 'Отправить отзыв',
    sending: 'Отправляем…',
    skip: 'Пропустить',
    logout: 'Выйти',
    language: 'Язык',
    menu: 'Меню',
    my_excursions: 'Мои экскурсии',
    plan_cta_title: "Соберите свою прогулку",
    plan_cta_sub: "Мало времени? Маршрут, собранный под вас",
    plan_title: "Соберите прогулку",
    plan_sub: "Ответьте на три вопроса — и мы соберём маршрут для вас",
    q_time: "Сколько у вас времени?",
    q_interests: "Что вам интересно?",
    q_interests_hint: "Выберите любое — или пропустите",
    q_with: "С кем вы?",
    with_alone: "Один / одна",
    with_couple: "Вдвоём",
    with_friends: "С друзьями",
    with_kids: "С детьми",
    q_kids: "Игровой квест для детей или обычная экскурсия?",
    kids_quest: "Детский квест",
    regular_tour: "Обычная экскурсия",
    build: "Собрать прогулку",
    building: "Собираем прогулку…",
    your_walk: "Ваша прогулка",
    walk_summary: "Точек: {n} · ≈ {min} мин",
    walk_incl: "из них ≈ {min} мин пешком",
    too_short: "Оставили только главные точки — прогулка может занять чуть больше времени",
    kids_fallback: "Детский квест скоро появится — пока обычная экскурсия",
    change_answers: "Изменить ответы",
    key_point: "Главная точка",
    no_points: "Прогулки скоро появятся",
    t_30: "30 мин",
    t_45: "45 мин",
    t_60: "1 час",
    t_90: "1,5 часа",
    t_120: "2 часа",
    you_here: 'Маршрут от того места, где вы сейчас',
    loc_asking: 'Определяем ваше местоположение…',
    loc_denied: 'Разрешите доступ к геолокации, чтобы видеть себя на карте',
    loc_unavailable: 'Сейчас не удаётся определить ваше местоположение',
    loc_refresh: 'Обновить моё место',
    expand: 'Открыть на весь экран',
    leave_site: 'Вы покидаете «{name}» и переходите на сайт партнёра.',
    go: 'Перейти',
    cancel: 'Отмена',
  },
  ar: {
    tagline: 'اكتشف\nدبي\nبطريقة مختلفة',
    create_account: 'أنشئ حسابك',
    create_sub: 'جولات صوتية غامرة\nومغامرات تفاعلية في المدينة',
    welcome_back: 'مرحبًا بعودتك',
    welcome_sub: 'سجّل الدخول لمتابعة\nرحلتك الصوتية في دبي',
    your_name: 'اسمك',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    agree_1: 'أوافق على ',
    terms: 'شروط الخدمة',
    agree_and: ' و',
    privacy: 'سياسة الخصوصية',
    get_started: 'ابدأ الآن',
    sign_in: 'تسجيل الدخول',
    or: 'أو',
    have_account: 'لديك حساب بالفعل؟',
    new_here: 'جديد في Voice of Dubai؟',
    create_account_link: 'إنشاء حساب',
    forgot: 'نسيت كلمة المرور؟',
    err_fill: 'يرجى إدخال {fields}.',
    f_name: 'اسمك',
    f_email: 'بريدًا إلكترونيًا صحيحًا',
    f_password: 'كلمة مرور (6 أحرف على الأقل)',
    accept_terms: 'يرجى الموافقة على الشروط للمتابعة',
    loading: 'جارٍ التحميل…',
    choose_excursion: 'اختر جولتك',
    explore_listen: 'استكشف · استمع · اكتشف',
    audio_walk: 'جولة صوتية',
    quest: 'مغامرة',
    min: '{n} دقيقة',
    no_excursions: 'الجولات قادمة قريبًا',
    start: 'ابدأ',
    continue_tour: 'تابع',
    view_on_map: 'عرض على الخريطة',
    open_map: 'افتح الخريطة',
    route: 'المسار',
    stops: '{n} محطات',
    walk_km: '≈ {km} كم سيرًا',
    walk_time: '≈ {min} دقيقة سيرًا',
    open_in_google: 'افتح المسار في خرائط Google',
    step_of: 'الخطوة {n} من {total}',
    continue: 'متابعة',
    finish: 'إنهاء',
    check_answer: 'تحقق من الإجابة',
    next: 'التالي',
    your_answer: 'إجابتك…',
    select_one: 'اختر إجابة واحدة',
    correct: 'إجابة صحيحة!',
    wrong: 'ليست صحيحة — حاول مرة أخرى',
    wrong_sub: 'حاول مرة أخرى أو اضغط «التالي»',
    attempts_left: 'خطأ. المحاولات المتبقية: {n}',
    attempts_over: 'انتهت المحاولات. اضغط «التالي».',
    hint: 'تلميح',
    show_hint: 'إظهار التلميح (−{n} نقاط)',
    reveal_answer: 'إظهار الإجابة',
    correct_answer: 'الإجابة الصحيحة',
    fun_fact: 'هل تعلم؟',
    video: 'فيديو',
    offline: 'وضع عدم الاتصال',
    narration: 'السرد لهذه اللحظة',
    step_not_found: 'الخطوة غير موجودة',
    steps_not_loaded: 'تعذّر تحميل الخطوات',
    congrats: 'تهانينا!',
    finished_sub: 'لقد أكملت الجولة',
    points_of: '{n} من {max}',
    points: 'نقطة',
    rate: 'قيّم الجولة',
    to_excursions: 'العودة إلى الجولات',
    thanks_partner: 'شكرًا لوجودك معنا!',
    rate_title: 'قيّم الجولة',
    rate_sub: 'رأيك يساعدنا على التحسّن',
    rate_labels: ['', 'لم تعجبني', 'مقبولة', 'جيدة', 'أعجبتني', 'رائعة!'],
    your_comment: 'تعليقك',
    comment_ph: 'شارك انطباعاتك (اختياري)',
    send_review: 'إرسال التقييم',
    sending: 'جارٍ الإرسال…',
    skip: 'تخطٍّ',
    logout: 'تسجيل الخروج',
    language: 'اللغة',
    menu: 'القائمة',
    my_excursions: 'جولاتي',
    plan_cta_title: "صمّم جولتك بنفسك",
    plan_cta_sub: "وقتك ضيق؟ احصل على مسار مصمم لك",
    plan_title: "صمّم جولتك",
    plan_sub: "أجب عن ثلاثة أسئلة وسنعدّ لك مسارًا",
    q_time: "كم من الوقت لديك؟",
    q_interests: "ما الذي يهمك؟",
    q_interests_hint: "اختر ما تشاء — أو تخطَّ",
    q_with: "مع من أنت؟",
    with_alone: "بمفردي",
    with_couple: "مع شريكي",
    with_friends: "مع الأصدقاء",
    with_kids: "مع الأطفال",
    q_kids: "مغامرة ألعاب للأطفال أم جولة عادية؟",
    kids_quest: "مغامرة للأطفال",
    regular_tour: "جولة عادية",
    build: "صمّم جولتي",
    building: "جارٍ تصميم جولتك…",
    your_walk: "جولتك",
    walk_summary: "{n} محطات · ≈ {min} دقيقة",
    walk_incl: "منها ≈ {min} دقيقة سيرًا",
    too_short: "أبقينا المحطات الرئيسية فقط — قد تستغرق الجولة وقتًا أطول قليلًا",
    kids_fallback: "مغامرة الأطفال قادمة قريبًا — إليك الجولة العادية",
    change_answers: "تغيير الإجابات",
    key_point: "محطة رئيسية",
    no_points: "الجولات ستتوفر قريبًا",
    t_30: "30 دقيقة",
    t_45: "45 دقيقة",
    t_60: "ساعة",
    t_90: "ساعة ونصف",
    t_120: "ساعتان",
    you_here: 'المسار من موقعك الحالي',
    loc_asking: 'جارٍ تحديد موقعك…',
    loc_denied: 'اسمح بالوصول إلى الموقع لترى مكانك على الخريطة',
    loc_unavailable: 'تعذّر تحديد موقعك الآن',
    loc_refresh: 'تحديث موقعي',
    expand: 'عرض بملء الشاشة',
    leave_site: 'أنت تغادر «{name}» وتنتقل إلى موقع الشريك.',
    go: 'انتقال',
    cancel: 'إلغاء',
  },
};

function fill(s, vars) {
  if (!vars || typeof s !== 'string') return s;
  return s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));
}

// Interface strings by key
export function t(key, vars) {
  const lang = getLang();
  const s = (S[lang] && S[lang][key] !== undefined) ? S[lang][key] : (S.en[key] !== undefined ? S.en[key] : key);
  return fill(s, vars);
}

// Strings inside task widgets, keyed by their original Russian text
export function tr(ru, vars) {
  const lang = getLang();
  const row = TASK_STRINGS[ru];
  const s = lang === 'ru' ? ru : (row && (row[lang] || row.en)) || ru;
  return fill(s, vars);
}
